import {Api, ModelRow, StringUtil} from "@mcleod/core";
import {
    Button, ClickEvent, Component, Label, Snackbar, TableRow, TableRowDisplayEvent, Textbox, Toast
} from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { SmsFieldsContainer, SmsUtility } from "@mcleod/powerbroker";
import { AutogenLayoutSmsMessageDetails } from "./autogen/AutogenLayoutSmsMessageDetails";

export class SmsMessageDetails extends AutogenLayoutSmsMessageDetails {
    private _movementId:string;

    public set movementId(value: string) {
        if (value != this._movementId) {
        this._movementId = value;
        this.searchSmsMessages();
        }
    }

    private searchSmsMessages() {
        if (this._movementId != null)
            this.sourceCarrierDriverMessage.maxResults = 5;
            this.sourceCarrierDriverMessage.search({movement_id: this._movementId});
    }

    public doSmsSearch(movementId: string) {
        if (movementId) {
            this.sourceCarrierDriverMessage.maxResults = 5;
        this.mainDataSource.search({"movement_id": movementId});
        }
    }

    private getComponentById<T extends Component>(tableRow: TableRow, componentId: string): T {
        return tableRow.findComponentById(componentId) as T;
    }

    tableSmsMessagesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        tableRow.setAdditionalToolProps("pencil", { visible: SmsUtility.showEditButton(modelRow) });
        tableRow.setAdditionalToolProps("delete", { visible: SmsUtility.showDeleteButton(modelRow) });
        tableRow.setAdditionalToolProps("cancelSms", { visible: SmsUtility.showCancelButton(modelRow) });
        tableRow.setAdditionalToolProps("reply", { visible: false});
        tableRow.setAdditionalToolProps("resendMessage", { visible: false });
        tableRow.setAdditionalToolProps("markMessageStatus", { visible: SmsUtility.showMarkAsReadUnreadButton(modelRow), tooltip: SmsUtility.setMarkAsRead(modelRow), imageName: SmsUtility.setMarkAsReadImage(modelRow) });
        tableRow.contextMenuItems = () => SmsUtility.getContextMenuItems(tableRow,false);
        SmsUtility.setupDirectionTimeColumn(tableRow,modelRow);
        SmsUtility.setupStatusColumn(tableRow,modelRow);
        SmsUtility.setupToFromColumn(tableRow.findComponentById ( "labelRecipient") as Label ,modelRow);
        SmsUtility.setMediaComponents(tableRow, modelRow);
        SmsUtility.setupMessageColumn(tableRow.findComponentById("labelMessageText") as Label,modelRow);
        const labelRecipient = this.getComponentById<Label>(tableRow, "labelRecipient");
        if(labelRecipient && !StringUtil.isEmptyString(labelRecipient.text)){
            labelRecipient.fontBold = true;

        }
    }

    buttonPencilOnClick(event: ClickEvent) {
        const button = event.target as Button;
        const tableRow = this.tableSmsMessages.findTableRowFromAdditionalTool(button);
        const modelRow = tableRow.data;
        // Extract relevant data from the modelRow
        const smsMessageRowId = modelRow.get("parent_row_id");
        const driverName = modelRow.get("driver_name");
        const driverCell = modelRow.get("recipient");
        const messageText = modelRow.get("message_text");
        const deliveryMode = modelRow.get("delivery_mode");
        const hours = modelRow.get("hours");
        const recurrenceFrequency = modelRow.get("recurrence_frequency");
        const recurrenceTermination = modelRow.get("recurrence_termination");
        const token = modelRow.get("token");

        // Access parent components
        const parent = this.parent.parent;
        const parentTab = parent.getComponent(0).parent;

        // Update visibility and enable state of buttons
        const sendMessageBtn = this.owner.buttonSend as Button;
        sendMessageBtn.visible = false;

        const updateMessageBtn = this.owner.buttonUpdate as Button;
        updateMessageBtn.enabled = true;
        updateMessageBtn.visible = true;


        // Access and update components in parentTab
        const parentFields = parentTab.findComponentById("layoutSmsFieldsContainer") as SmsFieldsContainer;
        const labelMessageId = parentTab.findComponentById("labelMessageId") as Label;
        labelMessageId.text = smsMessageRowId;

        parentFields.textboxDriverName.text = driverName;
        parentFields.textboxCellPhoneNumber.text = driverCell;
        parentFields.textboxSendMessageDeliveryMode.text = this.convertModeValue(deliveryMode);
        parentFields.textboxHours.text = hours;
        parentFields.textboxResendFrequency.text = recurrenceFrequency;
        parentFields.textboxRecurrenceTermination.text = this.convertRecurrenceTerminationValue(recurrenceTermination);

        // Update message text
        const message = parentTab.findComponentById("smsMessageText") as Textbox;

        if (StringUtil.isEmptyString(token) || messageText.includes(token)) {
            message.text = messageText;
        } else {
            message.text = `${messageText}\n\n\n${token}`;
        }
        parentFields.textboxDriverName.focus();
    }


    async buttonDeleteOnClick(event: ClickEvent) {
        const button = event.target as Button;
        const tableRow = this.tableSmsMessages.findTableRowFromAdditionalTool(button);
        const modelRow = tableRow.data;
        await SmsUtility.processDeleteMessage(modelRow, tableRow);
    }

    async buttonCancelOnClick(event: ClickEvent) {
        const button = event.target as Button;
        const tableRow = this.tableSmsMessages.findTableRowFromAdditionalTool(button);
        const modelRow = tableRow.data;
        await SmsUtility.processCancelMessage(tableRow, modelRow, button);
    }

    buttonReplyOnClick(event: ClickEvent, selectedRow: TableRow) {
        const button = event.target as Button;
        const tableRow = this.tableSmsMessages.findTableRowFromAdditionalTool(button);
        const modelRow = tableRow.data;
        SmsUtility.showSendSmsMessageSlideout(SmsUtility.setLayoutProps(modelRow,false,true));
    }

    async buttonMarkMessageStatusOnClick(event: ClickEvent) {
        const button = event.target as Button;
        const tableRow = this.tableSmsMessages.findTableRowFromAdditionalTool(button);
        const modelRow = tableRow.data;
        await SmsUtility.updateMessageStatus(tableRow, modelRow);
        button.tooltip = SmsUtility.setMarkAsRead(modelRow);
        button.imageName = SmsUtility.setMarkAsReadImage(modelRow);
    }

    buttonResendOnClick(event: ClickEvent) {
        const button = event.target as Button;
        const tableRow = this.tableSmsMessages.findTableRowFromAdditionalTool(button);
        const modelRow = tableRow.data;
        SmsUtility.showSendSmsMessageSlideout(SmsUtility.setLayoutProps(modelRow, false, false));
    }

    async labelCancelOnClick(event: ClickEvent): Promise<void> {
        const labelCancel = event.target as Label;
        const tableRow = TableRow.getContainingTableRow(labelCancel);
        const modelRow = tableRow.data;
        const messageRowId = modelRow.get("parent_row_id");
        const labelDirection = tableRow.findComponentById("labelDirection") as Label;
        const labelMessageStatus = tableRow.findComponentById("labelMessageStatus") as Label;
        const labelEditMessage = tableRow.findComponentById("labelEditMessage") as Label;
        if (await CommonDialogs.showYesNo("Are you sure you want to cancel this SMS Message?", "Cancel SMS Message")) {
            try {
                const response = await Api.post("lme/powerbroker/cancel-sms-message", {
                    message_row_id: messageRowId
                });
                if (response?.data?.[0]?.message_cancelled) {
                    Toast.showSuccessToast("SMS message has been canceled.", null, null, {millisUntilDismissal: 5000});
                    labelCancel.visible = false;
                    labelDirection.text = "Canceled";
                    labelDirection.imageName = null;
                    labelMessageStatus.visible = false;
                    labelEditMessage.visible = false;
                } else {
                    Snackbar.showSnackbar("Failed to cancel message.");
                }
            } catch (error) {
                Snackbar.showSnackbar("Failed to cancel message.");
            }
        }
    }


    private convertModeValue(mode: string) {
        if(StringUtil.equalsIgnoreCase("N",mode)){
            mode = "Now";
        }else if(StringUtil.equalsIgnoreCase("P",mode)){
            mode = "Prior to pickup"
        }else if(StringUtil.equalsIgnoreCase("A",mode)){
            mode = "After pickup"
        }
        return mode;
    }

    private convertRecurrenceTerminationValue(recurrenceTermination: string) {
        if(StringUtil.equalsIgnoreCase("C",recurrenceTermination)){
            recurrenceTermination = "Consignee Arrival";
        }else if(StringUtil.equalsIgnoreCase("L",recurrenceTermination)){
            recurrenceTermination = "Load Delivery"
        }
        return recurrenceTermination;
    }


    async labelDeleteMessageOnClick(event: ClickEvent): Promise<void> {
        const labelDeleteMessage = event.target as Label;
        const tableRow = TableRow.getContainingTableRow(labelDeleteMessage);
        const modelRow = tableRow.data;
        const rowId = modelRow.get("parent_row_id");
        if(await CommonDialogs.showYesNo(" Are you sure you want to delete this SMS Message?", "Delete SMS Message")){
            try {
                const response = await Api.post("lme/powerbroker/delete-sms-message", {
                    row_id: rowId
                });
                if (response?.data?.[0]?.message_deleted) {
                    Toast.showSuccessToast("Message has been deleted.", null, null, {millisUntilDismissal: 5000});
                    tableRow.visible = false;
                }
            } catch (error) {
                Snackbar.showSnackbar("Failed to delete message.");
            }
        }
    }
}
